import type { OrderLeg } from '../services/orders.service';

export interface DriverRemovalItem {
  proNumber: number;
  legnum: number;
  oldDriver: string;
  newDriver: string;
}

/**
 * Build the list of driver removals to send to /orders/check-deadline-criteria
 * before saving the Order Details modal. Two paths produce removal items:
 *
 *   1. Direct field clear — editedLegs sets driver to '' on a leg that had one.
 *   2. Role shift — the leg holding the originate or terminate role moves to a
 *      different leg (via legtype/legAction2 edits or new-leg adds), and the
 *      new role-holder has no driver. The original driver is effectively
 *      unassigned from the deadline-relevant role even though no leg's `driver`
 *      field literally changed.
 *
 * Backend deadline criteria are keyed off o.originateleg / o.terminateleg, so a
 * role shift onto an unassigned leg is exactly the case the popup is meant to
 * catch. Role identifiers mirror buildLegSavePayload (legtype === 'PICKUP' for
 * originate, legAction2 === 'TERMINATE' for terminate) so the frontend's
 * attribution matches what the v1 backend will write.
 *
 * Items are deduped by (legnum, oldDriver) so the same removal isn't double-
 * counted when the per-leg diff already covered the role-holder.
 */
export function buildDriverRemovalsForDeadlineCheck(args: {
  pronumber: number;
  originalLegs: OrderLeg[];
  currentLegs: OrderLeg[];
  editedLegs: Record<number, Partial<OrderLeg>>;
}): DriverRemovalItem[] {
  const { pronumber, originalLegs, currentLegs, editedLegs } = args;

  const eff = (l: OrderLeg, field: 'legtype' | 'legAction2' | 'driver'): string =>
    String((editedLegs[l.legrecno]?.[field] as string | undefined) ?? l[field] ?? '');

  // Path 1: direct driver-field clears.
  const removals: DriverRemovalItem[] = Object.entries(editedLegs)
    .filter(([legrecnoStr, changes]) => {
      const leg = currentLegs.find(l => l.legrecno === Number(legrecnoStr));
      const oldDriver = leg?.driver || '';
      const newDriver = changes.driver !== undefined ? (changes.driver || '') : oldDriver;
      return oldDriver.trim() !== '' && newDriver.trim() === '';
    })
    .map(([legrecnoStr]) => {
      const leg = currentLegs.find(l => l.legrecno === Number(legrecnoStr));
      return {
        proNumber: pronumber,
        legnum: leg?.legnum ?? Number(legrecnoStr),
        oldDriver: leg?.driver || '',
        newDriver: '',
      };
    });

  // Path 2: role shifts. For each role, compare the original role-holder
  // against the effective post-edit role-holder; synthesize a removal when
  // the role moved off a driver onto an unassigned leg (or disappeared).
  const roleSpecs: Array<{
    matchOriginal: (l: OrderLeg) => boolean;
    matchEffective: (l: OrderLeg) => boolean;
  }> = [
    {
      matchOriginal: l => (l.legAction2 || '') === 'TERMINATE',
      matchEffective: l => eff(l, 'legAction2') === 'TERMINATE',
    },
    {
      matchOriginal: l => (l.legtype || '') === 'PICKUP',
      matchEffective: l => eff(l, 'legtype') === 'PICKUP',
    },
  ];

  const seen = new Set(removals.map(r => `${r.legnum}|${r.oldDriver.trim()}`));
  for (const role of roleSpecs) {
    const origRoleLeg = originalLegs.find(role.matchOriginal);
    if (!origRoleLeg) continue;
    const origDriver = (origRoleLeg.driver || '').trim();
    if (!origDriver) continue;

    const postRoleLeg = currentLegs.find(role.matchEffective);
    // Same leg still holds the role → any driver-field clear is already covered above.
    if (postRoleLeg && postRoleLeg.legrecno === origRoleLeg.legrecno) continue;
    // Role moved to a different leg that has a driver → still dispatched.
    if (postRoleLeg && eff(postRoleLeg, 'driver').trim() !== '') continue;

    // Send the ORIGINAL leg's pre-save legnum so the backend can match it
    // against o.originateleg / o.terminateleg in legscurrent (which still
    // reflects pre-save state when the deadline check runs).
    const key = `${origRoleLeg.legnum}|${origDriver}`;
    if (seen.has(key)) continue;
    seen.add(key);
    removals.push({
      proNumber: pronumber,
      legnum: origRoleLeg.legnum,
      oldDriver: origDriver,
      newDriver: '',
    });
  }

  return removals;
}
